import { reverse } from 'named-urls'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import { ButtonClean, Toasts, useModalContext } from '@fourthwall/components'
import { routing } from '@utils/routing'

import type { Product } from '../../../../../../../__models__'
import { PRODUCTION_METHODS } from '../../../../../../../consts/productionMethods'
import { useOfferMigrate } from '../../../../../hooks/useOfferMigrateMutation/useOfferMigrateMutation'
import { useOfferMigrateProductionMethodOptionsQuery } from '../../../../../hooks/useOfferMigrateProductionMethodOptionsQuery/useOfferMigrateProductionMethodOptionsQuery'
import { ChangeProductionMethodModal } from '../../../../../modules/MigrateOffer/modules/ChangeProductionMethodModal/ChangeProductionMethodModal'
import { LABEL_BY_MANUFACTURING_SERVICE } from '../../../../Product.consts'
import { useOffer } from '../../../../hooks/useOffer'
import { useCustomizationV4Query } from '../../../hooks/useCustomizationV4Query'

type CatalogProductionMethodProps = {
  product: Product
  offerId: string
}

export const CatalogProductionMethod = ({ product, offerId }: CatalogProductionMethodProps) => {
  const navigate = useNavigate()
  const { offer } = useOffer(offerId)
  const { customizationV4Query } = useCustomizationV4Query([offer.customizationId!], {
    enabled: !!offer.customizationId,
  })
  const { open, close } = useModalContext()
  const { offerMigrateMutation } = useOfferMigrate()
  const { offerMigrateProductionMethodOptionsQuery } = useOfferMigrateProductionMethodOptionsQuery(
    [{ offerId }],
    {
      enabled: !!offer.customizationId,
    },
  )

  const isRecommendProductionMethod =
    customizationV4Query.data?.settings.recommendProductionMethod &&
    (product.productionMethod === 'DTG' || product.productionMethod === 'DTFX')

  const currentOption = {
    id: product.productionMethod,
    productId: product.id,
    productionMethod: PRODUCTION_METHODS[product.productionMethod],
  }

  const options = [
    currentOption,
    ...(offerMigrateProductionMethodOptionsQuery.data?.migrationOptions.map((option) => {
      return {
        id: option.productionMethod,
        productId: option.id,
        productionMethod: PRODUCTION_METHODS[option.productionMethod],
      }
    }) ?? []),
  ]

  const handleChangeProductionMethod = async () => {
    open(ChangeProductionMethodModal, {
      initialOption: currentOption,
      options,
      async onConfirm(option) {
        const { id } = await offerMigrateMutation.mutateAsync([
          { offerId },
          { productId: option.productId, migrateToNewProduct: true },
        ])

        if (!id) {
          throw new Error('Product id is missing')
        }

        Toasts.notify('Product has been successfully duplicated with a new production method', {
          type: 'success',
        })

        navigate(reverse(routing.products.all.product.self, { productId: id }))
      },
      onClose() {
        close()
      },
    })
  }

  if (!product) {
    return null
  }

  return (
    <>
      {product.productionMethod && (
        <p>
          {isRecommendProductionMethod ? (
            <>
              Printed by {LABEL_BY_MANUFACTURING_SERVICE[product.manufacturer]} (
              {PRODUCTION_METHODS[product.productionMethod].label})
            </>
          ) : (
            <>
              {PRODUCTION_METHODS[product.productionMethod].label} by{' '}
              {LABEL_BY_MANUFACTURING_SERVICE[product.manufacturer]}
            </>
          )}
        </p>
      )}
      {offerMigrateProductionMethodOptionsQuery.data &&
        offerMigrateProductionMethodOptionsQuery.data.migrationOptions.length > 0 && (
          <ButtonClean label="Change method" onClick={handleChangeProductionMethod} />
        )}
    </>
  )
}
